import { useState, useMemo, useCallback } from 'react';
import { formatFullTime, getCurrentTime, getNearestAvailableTime } from 'helpers/timeUtils';
import { CLOSE_TIME_LIMITS, RENT_TIME_LIMITS } from 'constants/rentTimeLimits';
import { SERVICE_TYPES } from 'constants/serviceTypes';

const getDefaultFormState = (currentDate, locationID, halls) => ({
  locationID,
  date: currentDate,
  startTime: formatFullTime(getNearestAvailableTime(getCurrentTime(), RENT_TIME_LIMITS)),
  endTime: formatFullTime(getNearestAvailableTime(getCurrentTime(1), CLOSE_TIME_LIMITS)),
  hallID: halls[0]?.id || '',
  clients: [{ fullName: '', price: null, subscription: null, locked: false }],
  coaches: [{ coach: '', price: '' }],
  group: '',
  service: '',
  comment: '',
});

const getEntryTypeIndex = (service) => {
  if (service === SERVICE_TYPES.GROUP) return 1;
  if (service === SERVICE_TYPES.RENT) return 2;
  return 0;
};

const formatPersonForForm = (p) => ({
  fullName: p.name ? `${p.surname} ${p.name}` : '',
  price: p.payment ? p.payment.amount.toString() : '',
  subscription: p.subscription,
  locked: false,
});

const formatCoachForForm = (c) => ({
  coach: c.name ? `${c.surname} ${c.name}` : '',
  price: c.payment ? c.payment.amount.toString() : '',
});

const populateFormFromEntry = (entry, halls, groups) => {
  const hallIndex = halls.findIndex((hall) => hall.id === entry.hall.id) || 0;
  const entryTypeIndex = getEntryTypeIndex(entry.service);

  let clients = [];
  let group = '';

  if (entry.groupID && entry.group) {
    group = entry.group;
    const fullGroup = groups.find((g) => g.id === entry.groupID);
    if (fullGroup) {
      clients = fullGroup.members.map((member) => {
        const payment = entry.payments.find((p) => p.clientID === member.id);
        return {
          fullName: `${member.surname} ${member.name}`,
          price: payment ? payment.amount.toString() : '',
          subscription: null,
          locked: true,
        };
      });
    }
  } else {
    clients =
      entry.participants && entry.participants.length > 0
        ? entry.participants.map((p) => formatPersonForForm(p))
        : [{ fullName: '', price: '', subscription: null, locked: false }];
  }

  const coaches =
    entry.coaches && entry.coaches.length > 0
      ? entry.coaches.map((c) => formatCoachForForm(c))
      : [{ coach: '', price: '' }];

  return {
    formData: {
      locationID: entry.location.id,
      date: entry.date,
      startTime: entry.startTime,
      endTime: entry.endTime,
      hallID: entry.hall.id,
      clients,
      coaches,
      group,
      service: entry.service,
      comment: entry.comment || '',
    },
    hallIndex,
    entryTypeIndex,
  };
};

export const useEntryFormLogic = ({ currentDate, locationID, halls, groups, editingEntry, allClients, allCoaches }) => {
  const isEditing = Boolean(editingEntry);

  const [entryTypeIndex, setEntryTypeIndex] = useState(0);
  const [hallIndex, setHallIndex] = useState(0);

  const currentLocationGroups = useMemo(() => groups.filter((g) => g.locationID === locationID), [groups, locationID]);

  const initialValues = useMemo(() => {
    if (isEditing && editingEntry && halls.length > 0) {
      const {
        formData,
        hallIndex: editHallIndex,
        entryTypeIndex: editEntryTypeIndex,
      } = populateFormFromEntry(editingEntry, halls, currentLocationGroups);

      setHallIndex(editHallIndex);
      setEntryTypeIndex(editEntryTypeIndex);

      return formData;
    }

    return getDefaultFormState(currentDate, locationID, halls);
  }, [isEditing, editingEntry, currentDate, locationID, halls, currentLocationGroups]);

  const transformSubmitData = useCallback(
    (values) => {
      const cleanedCoaches = values.coaches.filter(({ coach }) => coach.trim() !== '');
      const cleanedClients = values.clients.filter(({ fullName }) => fullName.trim() !== '');

      const unknownCoachNotes = [];
      const unknownClientNotes = [];

      const transformedCoaches = cleanedCoaches.map(({ coach, price }, index) => {
        const foundCoach = allCoaches.find(({ surname, name }) => `${surname} ${name}` === coach);
        const coachID = foundCoach?.id ?? 0;

        if (!foundCoach) {
          unknownCoachNotes.push(`Тренер${cleanedCoaches.length > 1 ? ' ' + (index + 1) : ''}: ${coach}`);
        }

        return {
          id: coachID,
          price: price || null,
        };
      });

      const transformedClients = cleanedClients.map(({ fullName, price, subscription }, index) => {
        const foundClient = allClients.find(({ surname, name }) => `${surname} ${name}` === fullName);
        const clientID = foundClient?.id ?? 0;

        if (!foundClient) {
          unknownClientNotes.push(`Клієнт${cleanedClients.length > 1 ? ' ' + (index + 1) : ''}: ${fullName}`);
        }

        return {
          id: clientID,
          price: price || null,
          subscriptionID: subscription?.id || null,
        };
      });

      const groupID = currentLocationGroups.find(({ name }) => values.group === name)?.id;

      const formComment = values.comment?.trim();
      const autoComment = unknownCoachNotes.concat(unknownClientNotes).join('; ');
      const fullComment = [formComment, autoComment].filter(Boolean).join('. ').trim();

      return {
        ...values,
        startTime: values.startTime.slice(0, 5),
        endTime: values.endTime?.slice(0, 5) || null,
        clients: transformedClients,
        coaches: transformedCoaches,
        groupID: groupID,
        comment: fullComment || '',
      };
    },
    [allClients, allCoaches, currentLocationGroups]
  );

  return {
    initialValues,
    transformSubmitData,
    entryTypeIndex,
    setEntryTypeIndex,
    hallIndex,
    setHallIndex,
    currentLocationGroups,
  };
};
